import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import {
  faSort,
  faSortUp,
  faSortDown,
  faCirclePlus,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmModal from "../../components/ConfirmModal";
import NotificationModal from "../../components/NotificationModal";
import ErrorModal from "../../components/ErrorModal";
import Modal from "../../components/Modal";

type User = {
  id: number;
  employee_id: number;
  name: string;
  name_en: string;
  email: string;
  join_date: string;
};

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof User;
    direction: "asc" | "desc" | null;
  }>({ key: "id", direction: null });

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isUserInvitationModalOpen, setIsUserInvitationModalOpen] =
    useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sendingInvitation, setSendingInvitation] = useState(false);

  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    text: string;
  }>({ isOpen: false, title: "", text: "" });

  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    title: string;
    text: string;
  }>({ isOpen: false, title: "", text: "" });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get<User[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/main/user-setting/get-user-list`,
        { withCredentials: true }
      );
      const sortedUserData = [...response.data].sort((a, b) => {
        if (a.employee_id == null) return 1;
        if (b.employee_id == null) return -1;
        return String(a.employee_id).localeCompare(String(b.employee_id));
      });
      setUsers(sortedUserData);
    } catch (error) {
      console.error("ユーザー取得エラー:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;
    try {
      setDeleting(true);
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/main/user-setting/delete/${selectedUser.id}`,
        { withCredentials: true }
      );
      fetchUsers();
      setNotification({
        isOpen: true,
        title: "削除成功",
        text: "ユーザーの削除が完了しました。",
      });
    } catch (error) {
      setNotification({
        isOpen: true,
        title: "削除失敗",
        text: "予期せぬエラーが発生しました。システム管理者に連絡してください。",
      });
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setSelectedUser(null);
    }
  };

  const sortedData = [...users].sort((a, b) => {
    if (sortConfig.direction === null) return 0;
    let [aValue, bValue] = [a[sortConfig.key], b[sortConfig.key]];

    const aIsEmpty = aValue == null || aValue === "";
    const bIsEmpty = bValue == null || bValue === "";

    if (aIsEmpty && bIsEmpty) return 0; // 両方空なら順序維持
    if (aIsEmpty) return sortConfig.direction === "asc" ? 1 : -1;
    if (bIsEmpty) return sortConfig.direction === "asc" ? -1 : 1;

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const requestSort = (key: keyof User) => {
    let direction: "asc" | "desc" | null = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof User) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  const openUserInvitationModal = () => {
    setIsUserInvitationModalOpen(true);
  };

  const isValidEmail = (email: string) => {
    // 簡易的なメール形式チェック
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendInvitationMail = async () => {
    // 空のときは処理を中断
    if (!email || email.trim() === "") {
      setEmailError("メールアドレスを入力してください");
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("正しいメールアドレスの形式で入力してください");
      return;
    }

    if (users.some((user) => user.email === email)) {
      setEmailError("入力されたメールアドレスは登録済みです");
      return;
    }

    try {
      setEmailError(""); // 問題なければエラー消す
      setSendingInvitation(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/main/system-admin/send-invitation-mail`,
        { email: email },
        { withCredentials: true }
      );
      await fetchUsers();
      setNotification({
        isOpen: true,
        title: "送信成功",
        text: "新規ユーザに招待メールを送信しました。",
      });
      setEmail("");
      setIsUserInvitationModalOpen(false);
    } catch (error) {
      console.error("招待メール送信エラー:", error);
      setErrorModal({
        isOpen: true,
        title: "送信失敗",
        text: "新規ユーザの招待メール送信に失敗しました。",
      });
    } finally {
      setSendingInvitation(false);
    }
  };

  return (
    <Layout>
      <div className="p-10 bg-white rounded-3xl">
        {loading || deleting ? (
          <>
            <div className="flex mb-4">
              <div className="w-10 h-10 bg-slate-200 rounded animate-pulse mr-4"></div>
              <div className="w-64 h-6 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="flex items-center justify-end mb-4">
              <div className="w-32 h-10 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="overflow-auto max-h-[70vh] border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <div className="p-6 space-y-4">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="w-10 h-10 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex mb-4">
              <div className="title-text font-black">ユーザ管理ページ</div>
            </div>
            <div className="flex items-center justify-end mb-4">
              <button
                onClick={() => openUserInvitationModal()}
                className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
              >
                <FontAwesomeIcon icon={faCirclePlus} className="mr-2" />
                ユーザー追加
              </button>
            </div>
            <div className="overflow-auto max-h-[70vh] border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <table className="min-w-full text-slate-600 border-collapse table-auto">
                <thead className="sticky top-0 bg-slate-100 border-b-2 border-slate-300">
                  <tr>
                    {[
                      { key: "employee_id", label: "社員番号" },
                      { key: "name", label: "名前" },
                      { key: "name_en", label: "名前（英語）" },
                      { key: "email", label: "メールアドレス" },
                      { key: "join_date", label: "入社日" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={() => requestSort(col.key as keyof User)}
                        className="py-4 px-2 cursor-pointer text-center"
                      >
                        <div className="flex items-center justify-center">
                          {col.label}
                          <FontAwesomeIcon
                            icon={getSortIcon(col.key as keyof User)}
                            className="ml-1"
                          />
                        </div>
                      </th>
                    ))}
                    <th className="py-2 px-4 text-center">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((user, index) => (
                    <tr
                      key={user.id}
                      className={`${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50"
                      } hover:bg-[#E4F4F7] cursor-pointer text-center`}
                    >
                      <td className="py-2 px-4 text-center">
                        {user.employee_id}
                      </td>
                      <td className="py-2 px-4 text-center">{user.name}</td>
                      <td className="py-2 px-4 text-center">{user.name_en}</td>
                      <td className="py-2 px-4 text-center">{user.email}</td>
                      <td className="py-2 px-4 text-center">
                        {user.join_date}
                      </td>
                      <td className="py-2 px-4 space-x-2 text-center">
                        <button
                          onClick={() => navigate(`/main/profile/${user.id}`)}
                          className="bg-slate-600 text-white py-1 px-4 rounded-3xl hover:bg-opacity-50"
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="bg-slate-600 text-white py-1 px-4 rounded-3xl hover:bg-opacity-50"
                        >
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* ユーザー招待をするモーダル */}
            <Modal
              isOpen={isUserInvitationModalOpen}
              onClose={() =>
                !sendingInvitation && setIsUserInvitationModalOpen(false)
              }
              title="新規ユーザを招待"
            >
              <div className="w-full max-w-md bg-white">
                {sendingInvitation ? (
                  // スケルトンスクリーン（送信中）
                  <div className="w-80">
                    <div className="w-24 h-5 bg-slate-200 rounded animate-pulse mb-2"></div>
                    <div className="w-full h-10 bg-slate-200 rounded-lg animate-pulse mb-4"></div>
                    <div className="flex items-center justify-end">
                      <div className="w-28 h-10 bg-slate-200 rounded-3xl animate-pulse"></div>
                    </div>
                    <p className="text-sm text-slate-500 mt-4 text-center">
                      招待メールを送信中...
                    </p>
                  </div>
                ) : (
                  // 通常の入力フォーム
                  <>
                    <label className="block text-gray-700">
                      メールアドレス
                    </label>
                    <div className="mb-2 w-80">
                      <input
                        type="text"
                        maxLength={100}
                        placeholder="example@example.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      {/* 赤字のエラーメッセージ */}
                      {emailError && (
                        <p className="text-red-500 text-xs mt-1">
                          *{emailError}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-end">
                      <button
                        onClick={handleSendInvitationMail}
                        className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 ml-2"
                      >
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="mr-2"
                        />
                        送信する
                      </button>
                    </div>
                  </>
                )}
              </div>
            </Modal>
          </>
        )}
      </div>

      {/* 削除確認モーダル - ロード中は非表示 */}
      {!loading && !deleting && (
        <ConfirmModal
          isOpen={confirmOpen}
          title="削除確認"
          text="本当に削除しますか？"
          onConfirm={confirmDelete}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {/* 成功通知モーダル - ロード中は非表示 */}
      {!loading && !deleting && (
        <NotificationModal
          isOpen={notification.isOpen}
          title={notification.title}
          text={notification.text}
          onClose={() =>
            setNotification({ isOpen: false, title: "", text: "" })
          }
        />
      )}

      {/* エラー通知モーダル - ロード中は非表示 */}
      {!loading && !deleting && (
        <ErrorModal
          isOpen={errorModal.isOpen}
          title={errorModal.title}
          text={errorModal.text}
          onClose={() => setErrorModal({ isOpen: false, title: "", text: "" })}
        />
      )}
    </Layout>
  );
};

export default AdminPage;
